import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, ReplaySubject } from 'rxjs';
import { concatMap, map, take } from 'rxjs/operators';

import { EmployeeProfileService } from './employee-profile.service';
import { NetIncome, ApiCreds, rawNetIncome } from '../models';
import { netIncomeUrl, netIncomeCleanKeys } from './constants';
import * as utils from './utils/service.utils';

@Injectable({ providedIn: 'root' })
export class NetIncomeService {
  /*** Observable cache of clean Employee Net Income */
  netIncomeSubject = new ReplaySubject<NetIncome>(1);

  /*** Observable for connecting to netIncomeSubject observable cache. */
  netIncome$ = this.netIncomeSubject.asObservable();

  constructor(private employeeProfileService: EmployeeProfileService, private http: HttpClient) {
    /** Refresh Data for Employee NetIncome */
    this.refresh();
  }

  /**
   ** Fetch Data from netIncome api with built url string.
   * 1. Build URL string.
   * ___
   *
   * 2. Fetch URL string and create Observable with Data.
   * ___
   */
  private fetchData(apiCreds: ApiCreds): Observable<rawNetIncome> {
    //* 1.
    const url = `${netIncomeUrl}/${apiCreds.bcl}/${apiCreds.id}`;
    //* 2.
    return this.http.get<rawNetIncome>(url);
  }

  /**
   * Rename raw API property names into Service-able property names
   * @param rawEmployee : rawProfile
   * 1. Extract usable NetIncome data from API response
   * ___
   * 2. Rename filtered NetIncome data with Clean Profile Keys.
   * ___
   * 3. Return cleaned NetIncome Data
   * ___
   * @param {rawNetIncomeData} rawNetIncomeData - raw data coming from Employee Service
   */
  private formatNetIncome(rawNetIncome: rawNetIncome): NetIncome {
    //* 1.
    const { success, errorMessage, ...filteredData } = rawNetIncome;
    //* 2.
    const cleanedNetIncome = utils.cleanApiKeys(netIncomeCleanKeys, filteredData);
    //* 3.
    return utils.keysToCamelCase(cleanedNetIncome);
  }

  // private fetchNetIncome() {
  //   return this.employeeProfileService.apiCreds$.pipe(
  //     //* 2.
  //     take(1),
  //     //* 3.
  //     concatMap(apiCreds => this.fetchData(apiCreds)),
  //     //* 4.
  //     map(rawNetIncome => this.formatNetIncome(rawNetIncome))
  //   );
  // }

  /**
   * 1. Pipe to employeeProfileService to grab Employee's API Credentials {bcl, employeeId}.
   * ___
   * 2. Take 1 to allow previous observable to complete and unsubscribe automatically.
   * ___
   * 3. Concat Map to bring inner fetch observable to top stream.
   * ___
   * 4. Map to result of top stream http get request.
   *    - Format rawNetIncome result.
   * ___
   * 5. Subscribe to store observable for netIncomeSubject.
   * ___
   */
  refresh(): void {
    //* 1.
    this.employeeProfileService.apiCreds$
      .pipe(
        //* 2.
        take(1),
        //* 3.
        concatMap(apiCreds => this.fetchData(apiCreds)),
        //* 4.
        map(rawNetIncome => this.formatNetIncome(rawNetIncome))
      )
      //* 5.
      .subscribe(netIncome => this.netIncomeSubject.next(netIncome));
  }
}
